import { supabase } from "./supabase";
import { formatCompositeId, parseCompositeId } from "@nrl/shared";

export function itemsFromCompositeIds(ids) {
  const grouped = new Map();
  for (const id of ids || []) {
    const parsed = parseCompositeId(id);
    if (!parsed) continue;
    const { rarity, legacyNum } = parsed;
    const key = formatCompositeId(parsed);
    if (grouped.has(key)) grouped.get(key).copies += 1;
    else grouped.set(key, { legacy_num: legacyNum, rarity, copies: 1 });
  }
  return [...grouped.values()];
}

export function compositeIdsFromItems(items) {
  return (items || []).flatMap(item => {
    try {
      const id = formatCompositeId({ rarity: item.rarity, legacyNum: item.legacy_num ?? item.legacyNum });
      return Number.isInteger(item.copies) && item.copies > 0 ? Array(item.copies).fill(id) : [];
    } catch { return []; }
  });
}

export function offerValue(items, chart) {
  return (items || []).reduce((sum, item) => sum + (chart?.[item.rarity] || 0) * item.copies, 0);
}

const errorCopy = {
  value_mismatch: "Your offer must match the listing's value exactly.",
  not_owned: "You no longer have those cards available to trade.",
  copies_cap: "That trade would push someone over the 50-copy limit for a card.",
  rate_limited: "Slow down — too many trade actions in the last hour.",
  listing_not_open: "That listing is no longer open.",
  already_countered: "You've already made a counter-offer on this listing.",
  offer_unavailable: "Those cards are no longer available, so the trade was called off.",
  too_many_listings: "You can have up to 5 open listings at a time.",
  counter_not_pending: "That counter-offer has already been resolved.",
  own_listing: "You can't counter your own listing.",
  not_found: "That listing isn't available.",
  unknown_card: "Something's wrong with those cards — refresh and try again.",
  invalid_items: "Something's wrong with those cards — refresh and try again.",
  network_error: "Couldn't reach the server — check your connection.",
};
export function tradeErrorCopy(code) {
  return errorCopy[code] || "Something went wrong — try again.";
}
const tokens = new Set([...Object.keys(errorCopy), "forbidden"]);
async function request(call) {
  try {
    const { data, error } = await call();
    if (error) {
      console.warn("Trade request refused", error);
      const token = error.message?.split(":")[0].trim();
      return { ok: false, code: tokens.has(token) ? token : "server_error" };
    }
    if (data?.ok === false) return { ok: false, code: "offer_unavailable", side: data.side };
    return { ...data, ok: true };
  } catch (error) {
    console.warn("Unable to reach trading server", error);
    return { ok: false, code: "network_error" };
  }
}
function mapRow(row) {
  return {
    id: row.id, userId: row.user_id, displayName: row.display_name, avatar: row.avatar,
    isMine: row.is_mine, status: row.status, valuePts: row.value_pts, createdAt: row.created_at,
    items: (row.items || []).map(item => ({ legacyNum: item.legacy_num, rarity: item.rarity, copies: item.copies, name: item.name, team: item.team })),
    counters: (row.counters || []).map(mapRow),
    pendingCounters: row.pending_counters, acceptedCounterId: row.accepted_counter_id, cancelReason: row.cancel_reason,
  };
}
export async function fetchTradeBoard() {
  const result = await request(() => supabase.rpc("get_trade_board", {}));
  if (!result.ok) return result;
  return { ok: true, listings: (result.listings || []).map(mapRow), myRecent: (result.my_recent || []).map(mapRow), valueChart: result.value_chart || {}, tradesCompleted: result.trades_completed || 0 };
}
export async function postListing(items) {
  return request(() => supabase.rpc("post_trade_listing", { p_items: items }));
}
export async function cancelListing(listingId) {
  return request(() => supabase.rpc("cancel_trade_listing", { p_listing_id: listingId }));
}
export async function postCounter(listingId, items) {
  return request(() => supabase.rpc("post_trade_counter", { p_listing_id: listingId, p_items: items }));
}
export async function withdrawCounter(counterId) {
  return request(() => supabase.rpc("withdraw_trade_counter", { p_counter_id: counterId }));
}
export async function declineCounter(counterId) {
  return request(() => supabase.rpc("decline_trade_counter", { p_counter_id: counterId }));
}
export async function acceptCounter(counterId) {
  return request(() => supabase.rpc("accept_trade_counter", { p_counter_id: counterId }));
}
